import { deriveAmounts, MAX_MONEY } from '../salary.js';
/**
 * Payroll calculation engine (FR-PAY-7…10, FR-PAY-13, FR-PAY-15, BR-PAY-3, ADR-061). Pure
 * functions shared by the API run service and the web preview, so both compute the same figures.
 *
 * Amounts are whole taka. Prorated amounts are summed exactly (integer numerators over a common
 * denominator) and rounded once per line item, half up. Dates are ISO `YYYY-MM-DD` strings.
 */
/** Line flags raised by the engine (FR-PAY-8). Run checks (FR-PAY-16) add their own later. */
export const PAY_LINE_FLAGS = ['DEDUCTION_SPILLOVER', 'NEGATIVE_NET'];
/** Deductions entered per run (FR-PAY-2…4); LWP comes from `lwpDays`. */
export const PAY_DEDUCTION_INPUTS = ['AIT', 'PF', 'ADVANCE', 'FOOD'];
/** Additions entered in a monthly run (FR-PAY-4). */
export const PAY_ADDITION_INPUTS = ['BONUS', 'LTA', 'ARREAR'];
/** Components a special run pays (FR-PAY-13). */
export const SPECIAL_RUN_COMPONENTS = ['FESTIVAL_BONUS', 'PROFIT_BONUS', 'BONUS', 'LTA'];
/**
 * The part each deduction is taken from first (FR-PAY-8): AIT and PF from the bank part, Advance
 * and Food from cash. Deductions are applied in this order.
 */
export const DEDUCTION_PREFERRED_PART = {
    AIT: 'BANK',
    PF: 'BANK',
    ADVANCE: 'CASH',
    FOOD: 'CASH',
};
const DAY_MS = 86_400_000;
function dayNumber(iso) {
    const ms = Date.parse(`${iso}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || Number.isNaN(ms)) {
        throw new RangeError(`Invalid date: ${iso}`);
    }
    return ms / DAY_MS;
}
function assertMoney(value, label) {
    if (!Number.isInteger(value) || value < 0 || value > MAX_MONEY) {
        throw new RangeError(`${label} must be a whole taka amount of 0 or more`);
    }
}
/** `n ÷ d` rounded half up, for integers n ≥ 0 and d > 0; exact for any size. */
export function divRoundHalfUp(n, d) {
    const bn = BigInt(n);
    const bd = BigInt(d);
    return Number((2n * bn + bd) / (2n * bd));
}
/** Inclusive number of days from `from` to `to`; 0 when `to` is before `from`. */
export function daysInclusive(from, to) {
    return Math.max(0, dayNumber(to) - dayNumber(from) + 1);
}
/**
 * Splits an addition between cash and bank in the salary's Cash : A/C proportion (FR-PAY-9):
 * cash is rounded half up and the remainder goes to bank.
 */
export function splitByPaymentMode(code, amount, salary) {
    assertMoney(amount, code);
    const { cashAmount } = deriveAmounts(salary.gross, salary.paymentMode, salary.cashAmount);
    const cash = divRoundHalfUp(amount * cashAmount, salary.gross);
    return { code, amount, cash, bank: amount - cash };
}
/** Special-run amount (FR-PAY-13): round(% × gross), half up, no proration (OQ-15). */
export function specialRunAmount(gross, percent) {
    assertMoney(gross, 'Gross');
    const hundredths = Math.round(percent * 100);
    if (hundredths <= 0 || hundredths > 20_000 || Math.abs(percent * 100 - hundredths) > 1e-6) {
        throw new RangeError('Percentage must be more than 0 and at most 200, up to 2 decimals');
    }
    return divRoundHalfUp(gross * hundredths, 10_000);
}
/**
 * Salary earned in a period and the LWP deduction (FR-PAY-7). Employed days run from
 * max(joining, start) to min(LWD, end); each salary segment is prorated by its own days at
 * gross ÷ calendar days. LWP days are spread over the segments in proportion to their days
 * (ADR-061), so LWP = Σ segment salary × LWP days ÷ employed days.
 */
export function calculateBasePay(input) {
    const { period } = input;
    const startDay = dayNumber(period.start);
    const endDay = dayNumber(period.end);
    const calendarDays = endDay - startDay + 1;
    if (calendarDays < 1)
        throw new RangeError('Period end is before its start');
    const fromDay = Math.max(startDay, dayNumber(input.joiningDate));
    const toDay = Math.min(endDay, input.lastWorkingDay ? dayNumber(input.lastWorkingDay) : endDay);
    const employedDays = Math.max(0, toDay - fromDay + 1);
    const lwpHalves = input.lwpDays * 2;
    if (!Number.isInteger(lwpHalves) || lwpHalves < 0 || input.lwpDays > employedDays) {
        throw new RangeError('LWP days must be 0 … employed days, in steps of 0.5');
    }
    // Exact numerators over `calendarDays`: Σ amount × segment days.
    let grossN = 0;
    let cashN = 0;
    const componentN = new Map();
    let remainderCode;
    input.salaries.forEach((segment, i) => {
        const segFrom = Math.max(fromDay, dayNumber(segment.effectiveDate));
        const next = input.salaries[i + 1];
        const segTo = next ? Math.min(toDay, dayNumber(next.effectiveDate) - 1) : toDay;
        if (i === 0 && employedDays > 0 && dayNumber(segment.effectiveDate) > fromDay) {
            throw new RangeError('No salary in effect on the first employed day');
        }
        if (next && dayNumber(next.effectiveDate) <= dayNumber(segment.effectiveDate)) {
            throw new RangeError('Salary segments must be in effective-date order');
        }
        const days = Math.max(0, segTo - segFrom + 1);
        assertMoney(segment.gross, 'Gross');
        const { cashAmount } = deriveAmounts(segment.gross, segment.paymentMode, segment.cashAmount);
        grossN += segment.gross * days;
        cashN += cashAmount * days;
        for (const c of segment.components) {
            assertMoney(c.amount, c.code);
            componentN.set(c.code, (componentN.get(c.code) ?? 0) + c.amount * days);
            if (c.isRemainder)
                remainderCode = c.code;
        }
    });
    if (employedDays > 0 && input.salaries.length === 0) {
        throw new RangeError('No salary in effect on the first employed day');
    }
    const total = divRoundHalfUp(grossN, calendarDays);
    const cash = divRoundHalfUp(cashN, calendarDays);
    const bank = total - cash;
    // A/C components: each rounded; the remainder component (Basic) absorbs the difference.
    const components = [];
    if (bank > 0) {
        if (remainderCode === undefined) {
            throw new RangeError('A/C salary has no remainder component');
        }
        let others = 0;
        for (const [code, n] of componentN) {
            if (code === remainderCode)
                continue;
            const amount = divRoundHalfUp(n, calendarDays);
            others += amount;
            components.push({ code, amount });
        }
        if (others > bank)
            throw new RangeError('A/C components exceed the A/C part');
        components.unshift({ code: remainderCode, amount: bank - others });
    }
    // LWP: Σ amount × days × (LWP days ÷ employed days), over `calendarDays`.
    const lwpDen = calendarDays * 2 * Math.max(employedDays, 1);
    const lwpTotal = divRoundHalfUp(grossN * lwpHalves, lwpDen);
    const lwpCash = divRoundHalfUp(cashN * lwpHalves, lwpDen);
    return {
        calendarDays,
        employedDays,
        lwpDays: input.lwpDays,
        payableDays: employedDays - input.lwpDays,
        salary: { total, cash, bank, components },
        lwp: { code: 'LWP', amount: lwpTotal, cash: lwpCash, bank: lwpTotal - lwpCash },
    };
}
/**
 * Takes deductions out of the parts (FR-PAY-8): from the preferred part while it lasts, then
 * from the other part (flagged). Whatever neither part covers stays on the preferred part and
 * makes the net negative (flagged; blocks submission).
 */
function applyDeductions(available, amounts, flags) {
    const result = [];
    for (const code of PAY_DEDUCTION_INPUTS) {
        const amount = amounts[code] ?? 0;
        assertMoney(amount, code);
        if (amount === 0)
            continue;
        const preferred = DEDUCTION_PREFERRED_PART[code] === 'CASH' ? 'cash' : 'bank';
        const other = preferred === 'cash' ? 'bank' : 'cash';
        const fromPreferred = Math.min(amount, Math.max(available[preferred], 0));
        const fromOther = Math.min(amount - fromPreferred, Math.max(available[other], 0));
        if (fromOther > 0)
            flags.add('DEDUCTION_SPILLOVER');
        const taken = { cash: 0, bank: 0 };
        taken[preferred] = amount - fromOther;
        taken[other] = fromOther;
        available.cash -= taken.cash;
        available.bank -= taken.bank;
        result.push({ code, amount, cash: taken.cash, bank: taken.bank });
    }
    return result;
}
function finishLine(base, splitSalary, additions, deductionAmounts) {
    const flags = new Set();
    const available = {
        cash: base.salary.cash - base.lwp.cash,
        bank: base.salary.bank - base.lwp.bank,
    };
    for (const a of additions) {
        available.cash += a.cash;
        available.bank += a.bank;
    }
    const deductions = applyDeductions(available, deductionAmounts, flags);
    const earningsTotal = base.salary.total + additions.reduce((s, a) => s + a.amount, 0);
    const deductionsTotal = base.lwp.amount + deductions.reduce((s, d) => s + d.amount, 0);
    const net = earningsTotal - deductionsTotal;
    if (net < 0)
        flags.add('NEGATIVE_NET');
    return {
        ...base,
        gross: splitSalary.gross,
        paymentMode: splitSalary.paymentMode,
        additions,
        deductions,
        earningsTotal,
        deductionsTotal,
        net,
        netCash: available.cash,
        netBank: available.bank,
        flags: PAY_LINE_FLAGS.filter((f) => flags.has(f)),
    };
}
/**
 * A monthly (or settlement) line (FR-PAY-7…10): prorated salary − LWP + additions − deductions.
 * Additions are split by the last salary applied in the period (FR-PAY-9).
 */
export function calculateMonthlyLine(input) {
    const base = calculateBasePay(input);
    const lwd = input.lastWorkingDay;
    const lastDay = lwd && lwd < input.period.end ? lwd : input.period.end;
    const splitSalary = input.salaries.filter((s) => s.effectiveDate <= lastDay).at(-1);
    if (!splitSalary)
        throw new RangeError('A pay line needs a salary');
    const additions = PAY_ADDITION_INPUTS.flatMap((code) => {
        const amount = input.additions?.[code] ?? 0;
        return amount === 0 ? [] : [splitByPaymentMode(code, amount, splitSalary)];
    });
    return finishLine(base, splitSalary, additions, input.deductions ?? {});
}
/**
 * A special-run line (FR-PAY-13): one addition of round(% × gross as of cutoff) — or HR's
 * override — split by the cutoff salary's Cash : A/C proportion. No salary, days or deductions.
 */
export function calculateSpecialLine(input) {
    const computed = specialRunAmount(input.salary.gross, input.percent);
    const amount = input.overrideAmount ?? computed;
    const base = {
        calendarDays: 0,
        employedDays: 0,
        lwpDays: 0,
        payableDays: 0,
        salary: { total: 0, cash: 0, bank: 0, components: [] },
        lwp: { code: 'LWP', amount: 0, cash: 0, bank: 0 },
    };
    const additions = amount === 0 ? [] : [splitByPaymentMode(input.component, amount, input.salary)];
    return finishLine(base, input.salary, additions, {});
}
/**
 * Arrear re-pricing (FR-PAY-15, ADR-054): each approved month is recalculated with the current
 * salary history (same employed and LWP days); arrear = Σ (re-priced − paid). The caller picks
 * the months with an approved monthly run and stores the detail in `arrear_calcs`.
 */
export function calculateArrear(months) {
    const detail = months.map((m) => {
        assertMoney(m.paid, 'Paid amount');
        const base = calculateBasePay(m);
        const repriced = base.salary.total - base.lwp.amount;
        return { period: m.period, paid: m.paid, repriced, difference: repriced - m.paid };
    });
    return { months: detail, total: detail.reduce((s, m) => s + m.difference, 0) };
}
//# sourceMappingURL=calc.js.map